if (!customElements.get('wlsn-buybox')) {
  class WLSNBuybox extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        buyboxRoot: 'wlsn-buybox',
        buyboxOptions: 'wlsn-buybox-product-options',
        rebuyWidgetBelowProductGalleryDesktop: 'js-rebuy-widget-below-product-gallery-desktop',
        rebuyWidgetOriginal: 'js-rebuy-widget-original'
      };

      this.bindMethods = {
        onOptionChange: this._onOptionChange.bind(this),
        onPopState: this._onPopState.bind(this),
        onPageUpdated: this._onPageUpdated.bind(this),
      };

      this.isProductPage =
        this.getAttribute('data-is-product-page') === 'true' && window.location.pathname.startsWith('/products/');

      this.templateName = this.getAttribute('data-template-name') || '';
      
      this.isUpdating = false;
    }

    /**
     * Get elements
     * @returns {Object} - Object containing DOM element references
     */
    get elements() {
      return {
        productOptions: this.querySelector(`${this.classes.buyboxOptions}`),
      };
    }

    /**
     * Connected callback method
     * @returns {void}
     */
    connectedCallback() {
      this._attachEventListeners();
      this._moveRebuyWidget();
      // Handle browser back/forward navigation
      window.addEventListener('popstate', this.bindMethods.onPopState);
      // Listen for page updates to reattach listeners
      window.addEventListener('wlsn:page:updated', this.bindMethods.onPageUpdated);
    }

    /**
     * Disconnected callback method
     * @returns {void}
     */
    disconnectedCallback() {
      this._detachEventListeners();
      window.removeEventListener('popstate', this.bindMethods.onPopState);
      window.removeEventListener('wlsn:page:updated', this.bindMethods.onPageUpdated);
    }

    /**
     * Attach event listeners
     * @returns {void}
     */
    _attachEventListeners() {
      const { productOptions } = this.elements;

      if (productOptions) {
        productOptions.addEventListener('wlsn:buybox:product-option-change', this.bindMethods.onOptionChange);
      }
    }

    /**
     * Detach event listeners
     * @returns {void}
     */
    _detachEventListeners() {
      const { productOptions } = this.elements;

      if (productOptions) {
        productOptions.removeEventListener('wlsn:buybox:product-option-change', this.bindMethods.onOptionChange);
      }
    }

    /**
     * Handle page updated event
     * @param {Event} event - Event object
     * @returns {void}
     */
    _onPageUpdated(event) {
      // Re-attach event listeners after page update
      this._detachEventListeners();
      this._attachEventListeners();
    }

    /**
     * Handle option change event
     * @param {Event} event - Event object
     * @returns {void}
     */
    _onOptionChange(event) {
      if (event.detail && event.detail.url) {
        this.isProductPage ? this._updateProductPage(event.detail.url) : this._updateProductContent(event.detail.url);
      }
    }

    /**
     * Handle pop state event
     * @param {Event} event - Event object
     * @returns {void}
     */
    _onPopState(event) {
      // Handle browser back/forward navigation
      this._updateProductPage(window.location.href, false);
    }

    /**
     * Sections whose DOM id matches these fragments are not swapped during SPA updates.
     * (Avoids touching header-group / footer-group and duplicate header-in-main instances.)
     * @param {string} sectionId - shopify-section-* element id
     * @returns {boolean}
     */
    _shouldSkipSpaSectionByDomId(sectionId) {
      if (!sectionId) return false;
      return (
        sectionId.includes('wlsn_header') ||
        sectionId.includes('wlsn_announcement_bar') ||
        sectionId.includes('wlsn_footer')
      );
    }

    /**
     * Update product page
     * @param {string} url - URL to update
     * @param {boolean} updateHistory - Whether to update history
     * @returns {void}
     */
    async _updateProductPage(url, updateHistory = true) {
      if (this.isUpdating) return;

      try {
        this.isUpdating = true;

        // Fetch the new product page
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch product page');

        const html = await response.text();

        // Parse the HTML
        const parser = new DOMParser();
        const newDocument = parser.parseFromString(html, 'text/html');

        // Find and update ALL Shopify sections in main
        const currentMain = document.querySelector('main');
        const newMain = newDocument.querySelector('main');

        if (!currentMain || !newMain) {
          throw new Error('Could not find main content container');
        }

        const currentSections = currentMain.querySelectorAll('[id^="shopify-section-"]');
        let replacedCount = 0;

        currentSections.forEach((currentSection) => {
          const sectionId = currentSection.id;
          if (!sectionId) return;

          // CSS.escape for weird id's
          const newSection = newMain.querySelector(`#${CSS.escape(sectionId)}`);

          if (newSection) {
            currentSection.innerHTML = newSection.innerHTML;
            this._executeScripts(currentSection);
            replacedCount++;
          }
        });

        // No sections were updated → very likely another template
        if (replacedCount === 0) {
          // Revert to "native" behavior – full navigation
          window.location.href = url;
          return;
        }

        // SPA scenario – successfully replaced sections
        if (updateHistory) {
          window.history.pushState({ path: url }, '', url);
        }

        window.dispatchEvent(new CustomEvent('wlsn:page:updated', { detail: { url } }));
      } catch (error) {
        console.error('Error updating product page:', error);
        window.location.href = url;
      } finally {
        this.isUpdating = false;
      }
    }

    /**
     * Update product content
     * @param {string} url - URL to update
     * @returns {void}
     */
    async _updateProductContent(url) {
      if (this.isUpdating) return;

      // If no template name is set or it's "none", skip the update
      if (!this.templateName || this.templateName === 'none') {
        return;
      }

      try {
        this.isUpdating = true;

        const response = await fetch(`${url}?view=${this.templateName}`);
        if (!response.ok) throw new Error('Failed to fetch product page');

        const html = await response.text();
        const parser = new DOMParser();
        const newDocument = parser.parseFromString(html, 'text/html');

        const currentMain = document.querySelector('main');
        const newMain = newDocument.querySelector('main');
        if (!currentMain || !newMain) {
          throw new Error('Could not find main content container');
        }

        // Scope to <main> only: document-wide section keys collide (header-group vs duplicate in main).
        const currentSections = currentMain.querySelectorAll('[id^="shopify-section-"]');
        const newSections = newMain.querySelectorAll('[id^="shopify-section-"]');

        // Create a map of current sections by section key
        const currentByKey = new Map();
        currentSections.forEach((section) => {
          if (this._shouldSkipSpaSectionByDomId(section.id)) return;
          const key = this._getSectionKeyFromId(section.id);
          if (!key) return;
          currentByKey.set(key, section);
        });

        // Loop through new sections and find pairs by key
        newSections.forEach((newSection) => {
          if (this._shouldSkipSpaSectionByDomId(newSection.id)) return;
          const key = this._getSectionKeyFromId(newSection.id);
          if (!key) return;

          const currentSection = currentByKey.get(key);
          if (!currentSection) return;

          currentSection.replaceWith(newSection);
          this._executeScripts(newSection);
        });

        window.dispatchEvent(
          new CustomEvent('wlsn:page:updated', {
            detail: { url: `${url}?view=${this.templateName}` },
          })
        );
      } catch (error) {
        console.error('Error updating product page:', error);
        window.location.href = url;
      } finally {
        this.isUpdating = false;
      }
    }

    /**
     * Get section key from id
     * @param {string} sectionId - Section id
     * @returns {string} - Section key
     */
    _getSectionKeyFromId(sectionId) {
      // Expected format:
      // shopify-section-template--25368737087808__wlsn_buybox_TGRqEC
      // shopify-section-template--25368737349952__wlsn_before_after_Dkc8QC

      const match = sectionId.match(/__(.+)_([A-Za-z0-9]+)$/);
      //             ^   ^    ^
      //             |   |    └ random suffix (e.g. TGRqEC)
      //             |   └ SECTION_KEY (can contain _ )
      //             └ everything after "__"

      return match ? match[1] : null; // e.g. "wlsn_buybox" or "wlsn_before_after"
    }

    /**
     * Move Rebuy widget to below product gallery if needed
     * @returns {void}
     */
    _moveRebuyWidget() {
      // Find all target containers for widgets below gallery
      const targetContainers = this.querySelectorAll(`.${this.classes.rebuyWidgetBelowProductGalleryDesktop}`);
      
      if (targetContainers.length === 0) return;

      targetContainers.forEach((targetContainer) => {
        const blockId = targetContainer.getAttribute('data-block-id');
        if (!blockId) return;

        // Find the original widget wrapper with matching block ID
        const originalWidget = this.querySelector(`.${this.classes.rebuyWidgetOriginal}[data-block-id="${blockId}"]`);
        if (!originalWidget) return;

        // Only move on desktop (min-width: 1024px)
        const moveWidget = () => {
          if (window.innerWidth >= 1024) {
            // Move widget content to target container
            const widgetContent = originalWidget.innerHTML;
            targetContainer.innerHTML = widgetContent;
            originalWidget.style.display = 'none';
          } else {
            // On mobile, keep widget in original position
            targetContainer.innerHTML = '';
            originalWidget.style.display = '';
          }
        };

        // Initial move
        moveWidget();

        // Handle resize
        let resizeTimeout;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(moveWidget, 100);
        });
      });
    }

    /**
     * Execute scripts in container element
     * @param {Element} container - Container element
     * @returns {void}
     */
    _executeScripts(container) {
      // Find all script tags in the container
      const scripts = container.querySelectorAll('script');

      scripts.forEach((oldScript) => {
        // Create a new script element
        const newScript = document.createElement('script');

        // Copy all attributes
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });

        // Copy the script content
        newScript.textContent = oldScript.textContent;

        // Replace the old script with the new one to trigger execution
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }
  }
  customElements.define('wlsn-buybox', WLSNBuybox);
}
